import { useState, useEffect } from 'react';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787';

interface Stats {
  content: {
    total: number;
    active: number;
    removed: number;
  };
  domains: {
    total: number;
    allowed: number;
    denied: number;
    unknown: number;
  };
  consentBreakdown: Array<{ consent_type: string; count: number }>;
  licenseBreakdown: Array<{ license: string; count: number }>;
  recentCrawls: Array<{
    domain: string;
    last_checked: string;
    consent_status: string;
    priority: string;
  }>;
  removals: {
    total: number;
    pending: number;
    approved: number;
    rejected: number;
  };
}

function AdminOverview() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/admin/stats`);
      if (!response.ok) throw new Error('Failed to fetch stats');
      const data = await response.json();
      setStats(data);
    } catch (err) {
      setError('Failed to load dashboard stats');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="loading-state">Loading dashboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-page">
        <div className="empty-state">
          <p>{error}</p>
          <button className="btn btn-primary mt-4" onClick={fetchStats}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <h1>Dashboard Overview</h1>
        <p>Monitor GoodFetch content indexing and crawler activity</p>
      </div>

      {/* Main Stats */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-card-label">Total Content</div>
          <div className="stat-card-value">{stats?.content.active.toLocaleString()}</div>
          <div className="stat-card-detail">
            {stats?.content.removed.toLocaleString()} removed
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-label">Domains Tracked</div>
          <div className="stat-card-value">{stats?.domains.total.toLocaleString()}</div>
          <div className="stat-card-detail">
            {stats?.domains.allowed} allowed, {stats?.domains.denied} denied
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-label">Pending Removals</div>
          <div className="stat-card-value">{stats?.removals.pending}</div>
          <div className="stat-card-detail">
            {stats?.removals.approved} approved, {stats?.removals.rejected} rejected
          </div>
        </div>
      </div>

      {/* Consent Breakdown */}
      <div className="admin-section">
        <div className="admin-section-header">
          <h2>Consent Types</h2>
        </div>
        <div className="card">
          {stats?.consentBreakdown && stats.consentBreakdown.length > 0 ? (
            stats.consentBreakdown.map((item, index) => (
              <div key={index} className="breakdown-item">
                <span className="breakdown-label">{item.consent_type}</span>
                <span className="breakdown-value">{item.count.toLocaleString()}</span>
              </div>
            ))
          ) : (
            <div className="empty-state">No content indexed yet</div>
          )}
        </div>
      </div>

      {/* License Breakdown */}
      <div className="admin-section">
        <div className="admin-section-header">
          <h2>Top Licenses</h2>
        </div>
        <div className="card">
          {stats?.licenseBreakdown && stats.licenseBreakdown.length > 0 ? (
            stats.licenseBreakdown.map((item, index) => (
              <div key={index} className="breakdown-item">
                <span className="breakdown-label">{item.license || 'Unknown'}</span>
                <span className="breakdown-value">{item.count.toLocaleString()}</span>
              </div>
            ))
          ) : (
            <div className="empty-state">No license data yet</div>
          )}
        </div>
      </div>

      {/* Recent Crawls */}
      <div className="admin-section">
        <div className="admin-section-header">
          <h2>Recent Crawl Activity</h2>
        </div>
        {stats?.recentCrawls && stats.recentCrawls.length > 0 ? (
          <table className="data-table">
            <thead>
              <tr>
                <th>Domain</th>
                <th>Status</th>
                <th>Priority</th>
                <th>Last Checked</th>
              </tr>
            </thead>
            <tbody>
              {stats.recentCrawls.map((crawl, index) => (
                <tr key={index}>
                  <td>{crawl.domain}</td>
                  <td>
                    <span className={`badge badge-${crawl.consent_status === 'allowed' ? 'success' : crawl.consent_status === 'denied' ? 'error' : 'warning'}`}>
                      {crawl.consent_status}
                    </span>
                  </td>
                  <td>{crawl.priority}</td>
                  <td>{new Date(crawl.last_checked).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="card empty-state">No crawl activity yet</div>
        )}
      </div>
    </div>
  );
}

export default AdminOverview;
